// Oberflächen-Manifest der Plattform: der Einstellungsbereich (/settings mit einer linken Liste, design-system §3.3),
// das eigene Profil und die Seite, auf der eine Einladung angenommen wird (ohne Anmeldung). Jede Seite ist über die
// Befehlspalette erreichbar; sichtbar ist nur, was die Person verwalten darf (Recht je Eintrag in settingsNav.ts).
// Wird von scripts/gen-registry.ts in Node geladen: Seiten nur per lazy-Import, keine Bausteine, kein CSS.
import { defineWebModule, type WebRoute } from '@web/module'
import { Settings2 } from 'lucide-react'
import { ALL_SETTINGS_ENTRIES } from './settingsNav'

const PAGES: Record<string, WebRoute['lazy']> = {
  company: () => import('./pages/CompanyPage'),
  features: () => import('./pages/FeaturesPage'),
  numbers: () => import('./pages/NumberRangesPage'),
  customFields: () => import('./pages/CustomFieldsPage'),
  people: () => import('./pages/PeoplePage'),
  security: () => import('./pages/SecurityPage'),
  mail: () => import('./pages/MailSettingsPage'),
  mailInbox: () => import('./pages/MailInboxPage'),
  roles: () => import('./pages/RolesPage'),
  rights: () => import('./pages/RightsPage'),
  assistant: () => import('./pages/AssistantSettingsPage'),
  audit: () => import('./pages/AuditPage'),
  privacy: () => import('./pages/PrivacyPage'),
  integrations: () => import('./pages/IntegrationsPage'),
  system: () => import('./pages/SystemPage'),
  profile: () => import('./pages/ProfilePage'),
}

export default defineWebModule({
  id: 'platform',
  routes: [
    { path: '/settings', title: 'Einstellungen', lazy: () => import('./pages/OverviewPage') },
    ...ALL_SETTINGS_ENTRIES.map(
      (entry): WebRoute => ({
        path: entry.to,
        title: entry.label,
        ...(entry.permission ? { permission: entry.permission } : {}),
        lazy: PAGES[entry.id] as WebRoute['lazy'],
      }),
    ),
    // Einladung annehmen: Wer den Link öffnet, hat noch kein Konto – die Seite liegt vor der Anmeldung
    { path: '/invitation', title: 'Einladung annehmen', layout: 'public', lazy: () => import('./pages/InvitationPage') },
    // Kennwort vergessen und über den Link aus der E-Mail neu festlegen (Token im Fragment, docs/anmeldung §6.3)
    { path: '/password/forgot', title: 'Kennwort vergessen', layout: 'public', lazy: () => import('./pages/PasswordForgotPage') },
    { path: '/password/reset', title: 'Neues Kennwort festlegen', layout: 'public', lazy: () => import('./pages/PasswordResetPage') },
    // Benachrichtigungen per E-Mail abbestellen (Link aus der Mail; erst die Schaltfläche ändert etwas, §4.8)
    { path: '/mail/unsubscribe', title: 'E-Mails abbestellen', layout: 'public', lazy: () => import('./pages/UnsubscribePage') },
    // Datenschutz in Merxo: vor und nach der Anmeldung lesbar (Anmeldeseite, Einladung, Profil; App-Store 5.1.1)
    { path: '/datenschutz', title: 'Datenschutz', layout: 'public', lazy: () => import('./pages/PrivacyNoticePage') },
  ],
  nav: [
    // EIN Eintrag führt in den Bereich; seine Seiten stehen dort in der linken Liste (und in der Übersicht)
    { section: 'settings', label: 'Alle Einstellungen', icon: Settings2, to: '/settings', order: 0 },
  ],
  commands: ALL_SETTINGS_ENTRIES.map((entry) => ({
    id: `platform.settings.${entry.id}`,
    label: entry.id === 'profile' || entry.id === 'rights' ? entry.label : `Einstellungen: ${entry.label}`,
    keywords: ['Einstellungen', 'Verwaltung', ...(entry.keywords ?? [])],
    icon: entry.icon,
    to: entry.to,
    ...(entry.permission ? { permission: entry.permission } : {}),
  })),
})
